import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, MessageCircle, Package, FileText, Home, Share2, Clock, Phone, Shield, AlertTriangle, XCircle } from 'lucide-react';
import Button from '../components/Button';
import Card from '../components/Card';
import StatusBadge from '../components/StatusBadge';
import { MP_PAYMENT_STATUSES } from '../utils/constants';

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(location.state?.order);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    if (!order) {
      navigate('/');
    }
    
    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, [order, navigate]);

  if (!order) return null;

  const isMercadoPago = order.payment_method === 'mercadopago';
  const paymentStatus = order.payment_status;
  const isPaymentApproved = paymentStatus === 'approved';
  const isPaymentRejected = paymentStatus === 'rejected';
  const isPaymentPending = paymentStatus === 'pending' || paymentStatus === 'in_process';

  const getSuccessIcon = () => {
    if (isMercadoPago) {
      if (isPaymentApproved) return <CheckCircle size={48} className="text-[#38B000]" />;
      if (isPaymentRejected) return <XCircle size={48} className="text-red-500" />;
      return <Clock size={48} className="text-amber-500" />;
    }
    return <CheckCircle size={48} className="text-[#38B000]" />;
  };

  const getSuccessTitle = () => {
    if (isMercadoPago) {
      if (isPaymentApproved) return '¡Pedido y pago confirmados!';
      if (isPaymentRejected) return 'Pago rechazado';
      return 'Pedido recibido, pago en proceso';
    }
    return '¡Pedido confirmado!';
  };

  const getSuccessMessage = () => {
    if (isMercadoPago) {
      if (isPaymentApproved) return 'Tu pago fue aprobado. Te contactaremos pronto para la entrega.';
      if (isPaymentRejected) return 'El pago fue rechazado. No te preocupes, puedes intentar con otro método.';
      return 'El pago está siendo procesado. Te notificaremos cuando se confirme.';
    }
    return 'Gracias por tu compra. Te contactaremos pronto.';
  };

  const generateWhatsAppMessage = () => {
    const items = order.items?.map(item => 
      `- ${item.quantity}x ${item.product_name} ($${item.price?.toLocaleString()} c/u)`
    ).join('\n');

    return encodeURIComponent(
      `¡Hola! Acabo de hacer un pedido en SammyGas\n\n` +
      `*Pedido #${order.code}*\n` +
      `*Total:* $${order.total?.toLocaleString()}\n\n` +
      `*Productos:*\n${items}\n\n` +
      `*Dirección:* ${order.address}\n` +
      `*Zona:* ${order.zone_name}\n\n` +
      `Gracias!`
    );
  };

  const handleShareWhatsApp = () => {
    const message = generateWhatsAppMessage();
    window.open(`https://wa.me/+56900000000?text=${message}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pedido SammyGas #${order.code}`,
          text: `Mi pedido en SammyGas por $${order.total?.toLocaleString()}`,
          url: window.location.href,
        });
      } catch (err) {
        // Error al compartir - ignorar silenciosamente
      }
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-24 px-4">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 rounded-full animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 50}%`,
                backgroundColor: ['#E85D04', '#38B000', '#1B263B'][Math.floor(Math.random() * 3)],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random()}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="max-w-md mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center animate-scaleIn ${
            isMercadoPago && isPaymentRejected ? 'bg-red-50' :
            isMercadoPago && isPaymentPending ? 'bg-amber-50' :
            'bg-green-50'
          }`}>
            {getSuccessIcon()}
          </div>
          <h1 className="text-3xl font-display font-bold text-[#1B263B] mb-2">
            {getSuccessTitle()}
          </h1>
          <p className="text-[#7A756F]">
            {getSuccessMessage()}
          </p>
        </div>

        {/* Order Code */}
        <Card className="mb-6 text-center border-2 border-primary/20">
          <p className="text-sm text-[#7A756F] mb-1">Código de pedido</p>
          <p className="text-4xl font-display font-bold text-primary tracking-wider">
            #{order.code}
          </p>
          <p className="text-xs text-[#9A9590] mt-2">
            Guarda este código para consultar tu pedido
          </p>
        </Card>

        {/* Order Details */}
        <Card className="mb-6">
          <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
            <FileText size={20} className="text-primary" />
            Detalles del pedido
          </h2>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[#7A756F]">Estado</span>
              <StatusBadge status={order.status || 'pendiente'} size="sm" />
            </div>

            <div className="flex justify-between">
              <span className="text-[#7A756F]">Fecha</span>
              <span className="font-medium text-[#1B263B]">
                {new Date(order.created_at).toLocaleDateString('es-CL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-[#7A756F]">Dirección</span>
              <span className="font-medium text-[#1B263B] text-right max-w-[60%]">
                {order.address}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-[#7A756F]">Pago</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-[#1B263B] capitalize">
                  {isMercadoPago ? 'MercadoPago' : order.payment_method?.replace('_', ' ')}
                </span>
                {isMercadoPago && paymentStatus && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    isPaymentApproved ? 'bg-green-100 text-green-800' :
                    isPaymentRejected ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {paymentStatus === 'approved' ? 'Aprobado' :
                     paymentStatus === 'rejected' ? 'Rechazado' :
                     paymentStatus === 'in_process' ? 'En proceso' : 'Pendiente'}
                  </span>
                )}
              </div>
            </div>

            <div className="border-t border-[#EFEDEA] pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="font-display font-bold text-lg text-[#1B263B]">Total</span>
                <span className="font-display font-bold text-2xl text-primary">
                  ${order.total?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          {isMercadoPago && isPaymentRejected && (
            <>
              <Link to="/checkout">
                <Button
                  size="lg"
                  fullWidth
                  variant="primary"
                  className="gap-2"
                >
                  Reintentar pago
                </Button>
              </Link>
              <p className="text-center text-sm text-[#7A756F]">
                Puedes volver al checkout y elegir otro método de pago
              </p>
            </>
          )}

          {(!isMercadoPago || !isPaymentRejected) && (
            <Button
              size="lg"
              fullWidth
              onClick={handleShareWhatsApp}
              className="gap-2 bg-[#38B000] hover:bg-[#2A8500]"
            >
              <MessageCircle size={20} />
              Enviar por WhatsApp
            </Button>
          )}

          {navigator.share && (
            <Button
              variant="outline"
              fullWidth
              onClick={handleNativeShare}
              className="gap-2"
            >
              <Share2 size={20} />
              Compartir pedido
            </Button>
          )}

          <Link to="/pedidos">
            <Button variant="ghost" fullWidth className="gap-2">
              <Package size={20} />
              Ver mis pedidos
            </Button>
          </Link>

          <Link to="/">
            <Button variant="ghost" fullWidth className="gap-2">
              <Home size={20} />
              Volver al inicio
            </Button>
          </Link>
        </div>

        {/* Info Box */}
        <div className={`mt-8 p-4 rounded-xl border ${
          isMercadoPago && isPaymentRejected ? 'bg-red-50 border-red-100' :
          isMercadoPago && isPaymentPending ? 'bg-amber-50 border-amber-100' :
          'bg-blue-50 border-blue-100'
        }`}>
          <div className="flex items-start gap-3">
            {isMercadoPago && isPaymentRejected ? (
              <XCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            ) : isMercadoPago && isPaymentPending ? (
              <Clock size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            ) : (
              <Clock size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={`font-semibold mb-1 ${
                isMercadoPago && isPaymentRejected ? 'text-red-900' :
                isMercadoPago && isPaymentPending ? 'text-amber-900' :
                'text-blue-900'
              }`}>
                {isMercadoPago && isPaymentRejected ? '¿Qué pasó con el pago?' :
                 isMercadoPago && isPaymentPending ? 'Pago en proceso' :
                 '¿Qué sigue?'}
              </p>
              <p className={`text-sm ${
                isMercadoPago && isPaymentRejected ? 'text-red-800' :
                isMercadoPago && isPaymentPending ? 'text-amber-800' :
                'text-blue-800'
              }`}>
                {isMercadoPago && isPaymentRejected
                  ? 'El pago fue rechazado por tu banco o emisor de tarjeta. Puedes intentar nuevamente con la misma tarjeta o usar otro método de pago.'
                  : isMercadoPago && isPaymentPending
                  ? 'Estamos esperando la confirmación del pago. Esto puede tomar unos minutos. Te notificaremos cuando se apruebe.'
                  : 'Revisaremos tu pedido y te contactaremos por teléfono o WhatsApp para confirmar la entrega.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
